import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import chatIconPng from "../../../../assets/Icons/avaris_chat_icon.png";
import chatInputSend from "../../../../assets/Icons/chat_input_send.png";
import closeChatIcon from "../../../../assets/Icons/close_chat_icon.png";

import './ChatButton.css';

/**
 * ChatButton - Floating toggle that opens the AVARIS AI chat preview UI.
 */
const ChatButton = ({ wipId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [sendPulse, setSendPulse] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Chat requests go through the backend's /api/chat routes, which add the
    // chat service's key, so the key isn't in this bundle.
    const defaultDenseK = Number.parseInt(process.env.REACT_APP_CHATBOT_TOP_K || '5', 10);
    const defaultSparseK = Number.parseInt(process.env.REACT_APP_CHATBOT_SPARSE_K || '2', 10);
    const sessionIdRef = useRef(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    if (!sessionIdRef.current) {
        const hasCrypto = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
        sessionIdRef.current = hasCrypto
            ? crypto.randomUUID()
            : `session-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
    }

    const createMessage = (role, text, overrides = {}) => ({
        id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        role,
        text,
        ...overrides,
    });

    const deriveAssistantText = (payload) => {
        if (!payload) {
            return 'No response received from chat service.';
        }

        if (typeof payload === 'string') {
            return payload;
        }

        if (payload && typeof payload === 'object' && payload.answer) {
            return payload.answer;
        }

        if (Array.isArray(payload)) {
            return payload
                .map(item => (typeof item === 'string' ? item : JSON.stringify(item)))
                .join('\n');
        }

        return (
            payload.text ||
            payload.message ||
            payload.response ||
            payload.result ||
            JSON.stringify(payload)
        );
    };

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [messages, isOpen]);

    // Initialize chat session when chat is opened for the first time
    useEffect(() => {
        if (!isOpen || isInitialized || !wipId) {
            return;
        }

        const initializeChat = async () => {
            try {
                console.log(`[ChatButton] Initializing chat session: ${sessionIdRef.current} for WIP: ${wipId}`);

                await axios.post('/api/chat/init', {
                    session_id: sessionIdRef.current,
                    wip_id: wipId,
                });
                console.log('[ChatButton] Chat session initialized successfully');
            } catch (error) {
                console.error('[ChatButton] Failed to initialize chat session:', error);
            } finally {
                setIsInitialized(true);
            }
        };

        void initializeChat();
    }, [isOpen, isInitialized, wipId]);

    const handleSend = async () => {
        const trimmedValue = inputValue.trim();

        if (!trimmedValue || isSending) {
            return;
        }

        const userMessage = createMessage('user', trimmedValue);
        const pendingMessage = createMessage('assistant', '', { isPending: true });
        const pendingMessageId = pendingMessage.id;

        setMessages(prevMessages => [...prevMessages, userMessage, pendingMessage]);
        setInputValue('');

        setSendPulse(true);
        setTimeout(() => setSendPulse(false), 350);

        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
            // Reset height immediately after send so the composer collapses
            inputRef.current.style.height = 'auto';
        }

        try {
            setIsSending(true);

            const request = {
                session_id: sessionIdRef.current,
                question: trimmedValue,
            };
            if (Number.isFinite(defaultDenseK) && defaultDenseK > 0) {
                request.k = defaultDenseK;
            }
            if (Number.isFinite(defaultSparseK) && defaultSparseK >= 0) {
                request.sparse_k = defaultSparseK;
            }

            const { data: payload } = await axios.post('/api/chat/query', request);
            const assistantText = deriveAssistantText(payload?.text_response ?? payload);

            setMessages(prevMessages => prevMessages.map(message => (
                message.id === pendingMessageId
                    ? { ...message, text: assistantText, isPending: false }
                    : message
            )));
        } catch (error) {
            console.error('[ChatButton] Failed to send message:', error);
            const notConfigured = error.response?.status === 503;
            setMessages(prevMessages => prevMessages.map(message => (
                message.id === pendingMessageId
                    ? {
                        ...message,
                        role: 'system',
                        text: notConfigured
                            ? 'Chat service is not configured. Please contact your administrator.'
                            : 'We could not reach the chat service. Please try again in a moment.',
                        isPending: false,
                    }
                    : message
            )));
        } finally {
            setIsSending(false);
        }
    };

    const handleInputKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void handleSend();
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
        }, 250);
    };

    return (
        <div className="wi-chat-widget">
            {isOpen && (
                <div className={`wi-chat-panel ${isClosing ? 'wi-chat-panel--closing' : ''}`} role="dialog" aria-label="Avaris AI chat panel">
                    <div className="wi-chat-header">
                        <span className="wi-chat-header-title">AVARIS AI</span>
                        <div className="wi-chat-header-controls">
                            {/* <button
                                type="button"
                                className="wi-chat-header-icon-btn"
                                aria-label="Edit work instruction"
                            >
                                <img
                                    src={penSquareIcon}
                                    alt=""
                                    aria-hidden="true"
                                    draggable="false"
                                />
                            </button> */}
                            <button
                                type="button"
                                className="wi-chat-close-btn"
                                onClick={handleClose}
                                aria-label="Close chat panel"
                            >
                                <img
                                    src={closeChatIcon}
                                    alt=""
                                    aria-hidden="true"
                                    draggable="false"
                                />
                            </button>
                        </div>
                    </div>

                    <div className="wi-chat-body" aria-live="polite" aria-atomic="false">
                        {messages.length === 0 ? (
                            <div className="wi-chat-empty" />
                        ) : (
                            <div className="wi-chat-messages" role="list">
                                {messages.map((message) => (
                                    <div
                                        className={`wi-chat-message-bubble wi-chat-message-bubble--${message.role}`}
                                        key={message.id}
                                        role="listitem"
                                    >
                                        <div className="wi-chat-message-text">
                                            {message.isPending ? (
                                                <div className="wi-chat-typing" aria-live="polite">
                                                    <span className="wi-chat-typing-label">Thinking</span>
                                                    <span className="wi-chat-typing-dots" aria-hidden="true">
                                                        <span className="wi-chat-typing-dot" />
                                                        <span className="wi-chat-typing-dot" />
                                                        <span className="wi-chat-typing-dot" />
                                                    </span>
                                                </div>
                                            ) : (
                                                message.text
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                    </div>

                    <div className="wi-chat-footer">
                        <div className="wi-chat-input">
                            {/* <button
                                type="button"
                                className="wi-chat-input-attach"
                                aria-label="Add attachments"
                            >
                                <img src={chatInputAttach} alt="" aria-hidden="true" draggable="false" />
                            </button> */}
                            <textarea
                                ref={inputRef}
                                className="wi-chat-input-field"
                                placeholder="Type here to ask anything"
                                aria-label="Type here to ask anything"
                                value={inputValue}
                                onChange={(event) => setInputValue(event.target.value)}
                                onKeyDown={handleInputKeyDown}
                                onBlur={(e) => {
                                    // Persist the event to prevent React from nullifying it
                                    e.persist();
                                    // Store target reference immediately before async operations
                                    const inputElement = e.target;
                                    const currentValue = inputValue;
                                    
                                    // Check where focus actually went after a short delay
                                    // This handles both click events (which may not set relatedTarget) and re-renders
                                    setTimeout(() => {
                                        const newActiveElement = document.activeElement;
                                        const input = inputRef.current || inputElement;
                                        
                                        // Check if focus moved to another input/textarea/select/button or contenteditable
                                        const isBlurringToAnotherInput = newActiveElement?.tagName === 'INPUT' || 
                                                                         newActiveElement?.tagName === 'TEXTAREA' ||
                                                                         newActiveElement?.tagName === 'SELECT' ||
                                                                         newActiveElement?.tagName === 'BUTTON' ||
                                                                         newActiveElement?.contentEditable === 'true' ||
                                                                         newActiveElement?.getAttribute('contenteditable') === 'true' ||
                                                                         newActiveElement?.tagName === 'IFRAME' ||
                                                                         newActiveElement?.closest('iframe') !== null;
                                        
                                        // Check if focus is inside an editor container (TinyMCE, contenteditable divs, etc)
                                        const editorContainers = document.querySelectorAll('[id*="editor"], [id*="Editor"], [class*="editor"], [class*="Editor"], #step-content, #step-content-container, .tox-tinymce');
                                        const isInsideEditor = Array.from(editorContainers).some(container => {
                                            return container.contains(newActiveElement) || container === newActiveElement;
                                        });
                                        
                                        // Only restore focus if:
                                        // 1. Focus didn't go to another input/textarea/etc
                                        // 2. Focus is not inside an editor container
                                        // 3. This input is not currently focused
                                        // 4. The input still exists in the DOM
                                        if (!isBlurringToAnotherInput && !isInsideEditor && input && document.activeElement !== input) {
                                            // Check if user clicked on a clickable element (like buttons, links, etc)
                                            const clickedElement = document.activeElement;
                                            const isClickableElement = clickedElement?.onclick !== null || 
                                                                      clickedElement?.getAttribute('role') === 'button' ||
                                                                      clickedElement?.classList.contains('clickable') ||
                                                                      clickedElement?.closest('button') !== null ||
                                                                      clickedElement?.closest('a') !== null;
                                            
                                            // Don't restore if user clicked on a clickable element
                                            if (!isClickableElement) {
                                                input.focus();
                                                // Restore cursor position if there's text
                                                if (input.setSelectionRange && currentValue) {
                                                    const cursorPos = Math.min(currentValue.length, input.value.length);
                                                    input.setSelectionRange(cursorPos, cursorPos);
                                                }
                                            }
                                        }
                                    }, 50); // Small delay to let click events complete
                                }}
                                onInput={(event) => {
                                    const target = event.currentTarget;
                                    const maxHeight = 200; // Match CSS max-height
                                    target.style.height = 'auto';
                                    const newHeight = Math.min(target.scrollHeight, maxHeight);
                                    target.style.height = `${newHeight}px`;
                                    // Enable scrolling if content exceeds max height
                                    target.style.overflowY = target.scrollHeight > maxHeight ? 'auto' : 'hidden';
                                }}
                                rows={1}
                            />
                            <button
                                type="button"
                                className={`wi-chat-input-send${sendPulse ? ' wi-chat-input-send--pulse' : ''}`}
                                aria-label="Send message"
                                onClick={() => { void handleSend(); }}
                                disabled={isSending || !inputValue.trim()}
                            >
                                <img src={chatInputSend} alt="" aria-hidden="true" draggable="false" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <button
                type="button"
                className={`wi-chat-toggle ${isOpen ? 'wi-chat-toggle--active' : ''}`}
                aria-label={isOpen ? 'Hide Avaris AI chat' : 'Open Avaris AI chat'}
                aria-expanded={isOpen}
                onClick={() => setIsOpen(prev => !prev)}
            >
                <img
                    src={chatIconPng}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                />
            </button>
        </div>
    );
};

export default ChatButton;

